import { isPlatformBrowser } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

import { Subscription } from "rxjs";
import { Swiper } from "@src/app/models/interfaces/swipeData.interface";
import { ImageService } from "src/app/servicios/image/image.service";

@Component({
  selector: "app-galeria-slider",
  templateUrl: "./galeria-slider.component.html",
  styleUrls: ["./galeria-slider.component.css"],
  imports: [ReactiveFormsModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GaleriaSliderComponent implements OnInit, AfterViewInit, OnDestroy {
  imagenes: string[] = [];
  numeroImagenes: number;
  imgsSuscripcion: Subscription;
  imgSuscripcion: Subscription;
  swipeData: Swiper = { start: {}, end: {} };
  mouseDown = false;
  startX: any;
  scrollLeft: any;
  pos = { top: 0, left: 0, x: 0, y: 0 };

  @ViewChild("galleryContainer") gallery: ElementRef<HTMLDivElement>;
  @ViewChild("divImg") divImg: ElementRef<HTMLDivElement>;

  constructor(
    private imageService: ImageService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId,
  ) {}

  ngOnInit(): void {
    this.imgsSuscripcion = this.imageService.getImagenes().subscribe((imgs) => {
      this.imagenes = imgs;
    });
    this.numeroImagenes = this.imageService.getNumImagenes();
  }

  ngAfterViewInit(): void {
    this.imgSuscripcion = this.imageService.getSeleccionada().subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.scrollIndex(this.imageService.getIndexActual());
      }
    });
  }

  ngOnDestroy(): void {
    if (this.imgsSuscripcion) this.imgsSuscripcion.unsubscribe();
    if (this.imgSuscripcion) this.imgSuscripcion.unsubscribe();
  }

  /**
   * La función `scrollBy` desplaza el elemento de la galería horizontalmente por un cierto número de
   * imágenes.
   * @param {number} signo - El parámetro "signo" es un número que determina la dirección y la
   * distancia del desplazamiento. Un valor positivo se desplazará hacia la derecha, mientras que un
   * valor negativo se desplazará hacia la izquierda. La magnitud del valor determina la distancia del
   * desplazamiento.
   */
  scrollBy(signo: number) {
    const imgWidth = this.divImg.nativeElement.offsetWidth;
    const galeriaWidth = this.gallery.nativeElement.offsetWidth;
    const numImagenes = Math.floor(galeriaWidth / imgWidth);
    this.gallery.nativeElement.scrollBy(signo * numImagenes * imgWidth, 0);
  }

  /**
   * La función `scrollIndex` desplaza la galería a un índice específico calculando la posición de
   * desplazamiento en función del ancho de las imágenes y el contenedor de la galería.
   * @param {number} index - El parámetro de índice es un número que representa la posición de la
   * imagen en la galería a la que desea desplazarse.
   */
  scrollIndex(index: number) {
    const imgWidth = this.divImg.nativeElement.offsetWidth;
    const galeriaWidth = this.gallery.nativeElement.offsetWidth;
    this.zone.runOutsideAngular(() => {
      this.gallery.nativeElement.scrollTo(index * imgWidth - (galeriaWidth - imgWidth) / 2, 0);
    });
  }

  /**
   * La función establece la imagen seleccionada y se desplaza al índice correspondiente.
   * @param {number} index - El parámetro de índice es un número que representa la posición de la
   * imagen en la que se hizo clic en una matriz o lista.
   * @returns No hay declaración de devolución en el fragmento de código proporcionado.
   */
  onClickedImage(index: number) {
    if (this.swipeData.end.swipeTime > 200) return;
    this.imageService.setSeleccionada(index);
    this.scrollIndex(index);
  }

  /**
   * La función llama al método `getImgSiguiente()` desde el objeto `imageService`.
   * Utilizado como función para el botón de pasar imagen.
   */
  siguiente() {
    this.imageService.getImgSiguiente();
    this.scrollIndex(this.imageService.getIndexActual());
  }

  /**
   * La función llama al método `getImgAnterior()` del objeto `imageService`.
   *Utilizado como función para el botón de imagen anterior.
   */
  anterior() {
    this.imageService.getImgAnterior();
    this.scrollIndex(this.imageService.getIndexActual());
  }

  /* La función `startDragging` es responsable de iniciar el comportamiento de arrastre del elemento de
  la galería. */
  startDragging(e) {
    this.gallery.nativeElement.style.setProperty("scroll-behavior", "auto");
    this.mouseDown = true;
    this.pos = {
      left: this.gallery.nativeElement.scrollLeft,
      top: this.gallery.nativeElement.scrollTop,
      x: e.clientX,
      y: e.clientY,
    };
    this.swipeData.start.swipeTime = new Date().getTime();
    e.stopPropagation();
  }

  /**
   * La función "stopDragging" se usa para manejar el final de un evento de arrastre, actualizar los
   * datos de deslizamiento y aplicar un comportamiento de desplazamiento suave.
   * @param {Event} e - El parámetro "e" es un objeto de evento que representa el evento que activó la
   * función. Por lo general, se pasa como un argumento cuando se llama a la función. En este caso, es
   * una instancia de la clase Evento.
   */
  stopDragging(e: Event) {
    this.mouseDown = false;
    this.swipeData.end.swipeTime = new Date().getTime() - this.swipeData.start.swipeTime;
    this.gallery.nativeElement.style.setProperty("scroll-behavior", "smooth");
    e.stopPropagation();
  }

  /**La función moveEvent actualiza la posición de desplazamiento de un elemento de la galería en
   * función del movimiento del mouse.
   * La función mueve el elemento de la galería horizontalmente según el movimiento del raton.
   * @param e - El parámetro "e" es un objeto de evento que representa el evento del mouse que activó
   * la función. Contiene información sobre el evento, como la propiedad clientX que representa la
   * coordenada horizontal del puntero del mouse en relación con la ventana gráfica.
   * @returns nada (indefinido) si la condición `if(!this.mouseDown)` es verdadera.
   */
  moveEvent(e) {
    if (!this.mouseDown) return;
    const dx = e.clientX - this.pos.x;
    this.gallery.nativeElement.scrollLeft = this.pos.left - dx;
    e.stopPropagation();
  }
}
